import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, User, CheckCircle2, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSchoolStudents } from '@/hooks/useSchoolAdmin';
import StudentDetailDrawer from '@/components/admin/StudentDetailDrawer';

interface CounselorViewProps {
  schoolId: string;
}

const CounselorView = ({ schoolId }: CounselorViewProps) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page on search change
  useEffect(() => { setPage(0); }, [debouncedSearch]);

  const { students, total, loading } = useSchoolStudents(schoolId, debouncedSearch, page);
  const totalPages = Math.ceil(total / 25);

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search students by name or email..."
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Results count */}
      <p className="text-xs text-muted-foreground">
        {total} student{total !== 1 ? 's' : ''} found
      </p>

      {/* Student List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      ) : students.length === 0 ? (
        <div className="glass-card p-8 text-center text-muted-foreground">
          <User className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <p>{debouncedSearch ? 'No students match your search.' : 'No students enrolled yet.'}</p>
        </div>
      ) : (
        <motion.div className="space-y-2" initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.03 } } }}>
          {students.map((student) => {
            const hasAssessment = student.assessment_count > 0;
            const isRecent = student.last_assessment && (Date.now() - new Date(student.last_assessment).getTime()) < 7 * 24 * 60 * 60 * 1000;

            return (
              <motion.button
                key={student.user_id}
                variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
                onClick={() => setSelectedStudent(student.user_id)}
                className="w-full glass-card p-4 flex items-center gap-4 text-left hover:border-primary/30 transition-colors group"
              >
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  hasAssessment ? 'bg-primary/10' : 'bg-secondary'
                }`}>
                  <User className={`w-5 h-5 ${hasAssessment ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">
                      {student.display_name || student.email.split('@')[0]}
                    </span>
                    {student.grade && (
                      <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-secondary">
                        {student.grade}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{student.email}</p>
                </div>

                {/* Assessment info */}
                <div className="text-right shrink-0">
                  {hasAssessment ? (
                    <>
                      <span className="text-xs text-foreground truncate block max-w-[140px]">{student.top_path || 'Completed'}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {student.last_assessment && new Date(student.last_assessment).toLocaleDateString()}
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">No assessment</span>
                  )}
                </div>

                {/* Status badge */}
                <div className="shrink-0">
                  {isRecent ? (
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">New</span>
                  ) : hasAssessment ? (
                    <CheckCircle2 className="w-4 h-4 text-accent" />
                  ) : (
                    <Clock className="w-4 h-4 text-muted-foreground/40" />
                  )}
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-4">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Student Detail Drawer */}
      <AnimatePresence>
        {selectedStudent && (
          <StudentDetailDrawer
            studentUserId={selectedStudent}
            schoolId={schoolId}
            onClose={() => setSelectedStudent(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CounselorView;